"use client";

import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Slot } from "@radix-ui/react-slot";

export type TextInputProps = ComponentPropsWithoutRef<"input"> & {
  invalid?: boolean;
};
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    { invalid, "aria-invalid": ariaInvalid, className = "", ...props },
    ref,
  ) {
    return (
      <input
        ref={ref}
        className={`dx-field ${className}`}
        aria-invalid={ariaInvalid ?? invalid}
        {...props}
      />
    );
  },
);

export type TextAreaProps = ComponentPropsWithoutRef<"textarea"> & {
  invalid?: boolean;
};
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    { invalid, "aria-invalid": ariaInvalid, className = "", ...props },
    ref,
  ) {
    return (
      <textarea
        ref={ref}
        className={`dx-field dx-textarea ${className}`}
        aria-invalid={ariaInvalid ?? invalid}
        {...props}
      />
    );
  },
);

export type SelectProps = ComponentPropsWithoutRef<"select"> & {
  invalid?: boolean;
};
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { invalid, "aria-invalid": ariaInvalid, className = "", ...props },
    ref,
  ) {
    return (
      <select
        ref={ref}
        className={`dx-field dx-select ${className}`}
        aria-invalid={ariaInvalid ?? invalid}
        {...props}
      />
    );
  },
);

export type ChoiceProps = Omit<ComponentPropsWithoutRef<"input">, "type"> & {
  invalid?: boolean;
};
export const ChoiceInput = forwardRef<
  HTMLInputElement,
  ChoiceProps & { type: "checkbox" | "radio" }
>(function ChoiceInput(
  { type, invalid, "aria-invalid": ariaInvalid, className = "", ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      className={`dx-choice dx-choice--${type} ${className}`}
      aria-invalid={ariaInvalid ?? invalid}
      {...props}
    />
  );
});
export const Checkbox = forwardRef<HTMLInputElement, ChoiceProps>(
  function Checkbox(
    { invalid, "aria-invalid": ariaInvalid, className = "", ...props },
    ref,
  ) {
    return (
      <input
        ref={ref}
        type="checkbox"
        className={`dx-choice dx-choice--checkbox ${className}`}
        aria-invalid={ariaInvalid ?? invalid}
        {...props}
      />
    );
  },
);
/** Native checkbox events with the published EllaDx Toggle appearance. */
export const Toggle = forwardRef<HTMLInputElement, ChoiceProps>(function Toggle(
  { invalid, "aria-invalid": ariaInvalid, className = "", ...props }, ref,
) {
  return <input {...props} ref={ref} type="checkbox" role="switch"
    className={`dx-toggle ${className}`} aria-invalid={ariaInvalid ?? invalid} />;
});
export const Radio = forwardRef<HTMLInputElement, ChoiceProps>(function Radio(
  { invalid, "aria-invalid": ariaInvalid, className = "", ...props },
  ref,
) {
  return (
    // aria-invalid is kept on the radio on purpose (pinned by the portal's
    // SharedPackageContracts test): callers mark an invalid choice on the input
    // itself and screen readers announce it, though ARIA scopes it to the
    // radiogroup.
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <input
      ref={ref}
      type="radio"
      className={`dx-choice dx-choice--radio ${className}`}
      data-invalid={ariaInvalid ?? invalid}
      aria-invalid={ariaInvalid ?? invalid}
      {...props}
    />
  );
});

export function FieldMessage({
  error = false,
  className = "",
  ...props
}: ComponentPropsWithoutRef<"p"> & { error?: boolean }) {
  return (
    <p
      className={`dx-field-message ${error ? "dx-field-message--error" : ""} ${className}`}
      {...props}
    />
  );
}

/** Share the field appearance while Radix keeps its own roles and events. */
export const FieldControl = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<typeof Slot> & {
    invalid?: boolean;
    asChild?: boolean;
  }
>(function FieldControl(
  { invalid, className = "", asChild: _asChild, ...props },
  ref,
) {
  return (
    <Slot
      {...props}
      ref={ref}
      className={`dx-field ${className}`}
      aria-invalid={props["aria-invalid"] ?? invalid}
    />
  );
});
export const ChoiceControl = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<typeof Slot> & {
    invalid?: boolean;
    asChild?: boolean;
  }
>(function ChoiceControl(
  { invalid, className = "", asChild: _asChild, ...props },
  ref,
) {
  return (
    <Slot
      {...props}
      ref={ref}
      className={`dx-choice dx-choice--custom ${className}`}
      data-invalid={invalid || undefined}
    />
  );
});

/** Native range behavior, including arrow-key adjustment, with shared focus. */
export const RangeInput = forwardRef<
  HTMLInputElement,
  Omit<ComponentPropsWithoutRef<"input">, "type">
>(function RangeInput({ className = "", ...props }, ref) {
  return (
    <input
      {...props}
      ref={ref}
      type="range"
      className={`dx-range ${className}`}
    />
  );
});
